import { forwardRef, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  label?: string
  error?: string
  hint?: string
  wrapperClassName?: string
  className?: string
  /** Valor numérico (já em %, ex.: 12.5 representa 12,5%). null = vazio. */
  value: number | null
  onChange: (next: number | null) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name?: string
  id?: string
  clearable?: boolean
  /** Casas decimais permitidas. Default 2. */
  decimals?: 0 | 1 | 2 | 3 | 4
  /** Máximo aceito (default 100 — porcentagem clássica). */
  max?: number
}

/**
 * Input para porcentagens (formatação com vírgula decimal + sufixo "%").
 *
 *  - Máscara incremental igual ao CurrencyInput: trata internamente como
 *    "centésimos" (ou centi/mili conforme `decimals`).
 *  - Aceita até `decimals` casas decimais (default 2).
 *  - onChange entrega number puro (ex.: 12.5 para "12,5%").
 *  - Clampa em `max` (default 100) no blur.
 */
export const PercentInput = forwardRef<HTMLInputElement, Props>(function PercentInput({
  label, error, hint, wrapperClassName, className,
  value, onChange, onBlur,
  placeholder, disabled, required, name, id,
  clearable = true, decimals = 2, max = 100,
}, ref) {
  const hasError = Boolean(error)
  const scale = Math.pow(10, decimals)

  const format = (num: number | null): string => {
    if (num === null || num === undefined || Number.isNaN(num)) return ''
    const units = Math.round(num * scale)
    return formatScaledBR(units, decimals)
  }
  const [text, setText] = useState<string>(() => format(value))
  const editingRef = useRef(false)

  useEffect(() => {
    if (editingRef.current) return
    setText(format(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    editingRef.current = true
    const onlyDigits = e.target.value.replace(/\D+/g, '')
    if (onlyDigits === '') {
      setText('')
      onChange(null)
      return
    }
    const trimmed = onlyDigits.replace(/^0+(?=\d)/, '')
    const units = parseInt(trimmed, 10)
    setText(formatScaledBR(units, decimals))
    onChange(units / scale)
  }

  function handleBlur() {
    editingRef.current = false
    let v = value
    if (v !== null && v !== undefined && typeof max === 'number' && v > max) {
      v = max
      onChange(v)
    }
    setText(format(v ?? null))
    onBlur?.()
  }

  function clear() {
    onChange(null)
    setText('')
  }

  const showClear = clearable && text !== '' && !disabled

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1" htmlFor={id}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}
      <div className={cn(
        'relative flex items-center bg-white rounded-md ring-1 transition',
        hasError ? 'ring-rose-300 focus-within:ring-rose-500' : 'ring-gray-200 focus-within:ring-brand-500 focus-within:ring-2',
        disabled && 'opacity-50 bg-gray-50',
      )}>
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode="decimal"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          className={cn(
            'w-full bg-transparent rounded-md text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none',
            'pl-3 pr-12 py-2 text-right',
            className,
          )}
        />
        <span className={cn('absolute text-xs text-gray-500 pointer-events-none', showClear ? 'right-9' : 'right-3')}>%</span>
        {showClear && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Limpar"
            onClick={clear}
            className="absolute right-2 cursor-pointer inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {(error || hint) && (
        <span className={cn('text-[11px] mt-1', hasError ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </span>
      )}
    </div>
  )
})

function formatScaledBR(units: number, decimals: number): string {
  if (decimals === 0) return units.toLocaleString('pt-BR')
  const sign = units < 0 ? '-' : ''
  const abs = Math.abs(units).toString().padStart(decimals + 1, '0')
  const integer = abs.slice(0, -decimals)
  const decimalPart = abs.slice(-decimals)
  const withDots = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `${sign}${withDots},${decimalPart}`
}
